<template>
  <VDialogV3
    ref="dialog"
    id="partner-card"
    position="right"

    :width="editMode ? '1250px' : '600px'"

    :entity="localPartner"
    :feed-entity-pointer="editMode ? partnerPointer : null"

    :context-menu="PartnersService.contextMenu"

    :tabs="editMode ? tabs : null"
    v-model:active-tab="activeTab"

    :hide-footer="editMode"
    :hide-feed="activeTab?.id === 'supplyOffers' || activeTab?.id === 'orders'"
    :submit-method="submit"

    @close="onClose"
  >

    <template #badges>
      <VBadge
        v-if="showDeclinedProductBadge"
        class="warning"
      >
        Есть отклонённые товары
      </VBadge>
    </template>

    <PartnerInfoSection
      v-if="!editMode"
      ref="infoSection"
      :local-partner="localPartner"
    />

    <template v-else>
      <PersonsSection
        v-if="activeTab.id === 'persons'"
        :localPartner="localPartner"
        @persons:updated="renewPartner"
      />

      <PartnerMainSection
        v-if="activeTab.id === 'main'"
        :localPartner="localPartner"
        @partner:updated="renewPartner"
      />

      <OrderPositionsRegistry
        v-if="activeTab.id === 'orders'"
        :params="{ seller: localPartner, buyer: $core.currentPartner }"
        class="fill"
        :hidden-columns="[
          'comment',
          'orderCategory',
          'seller',
          'buyer',
          'documentNumber',
          'desiredReceiptDate',
          'plannedShipmentDate',
          'payment'
        ]"
      />

      <SupplyOffersRegistry
        v-if="activeTab.id === 'supplyOffers'"
        :seller="localPartner"
      />

      <FilesList
        v-show="activeTab.id === 'files' || FilesService.isDragging"
        :entities="fileEntities"
      />
    </template>
  </VDialogV3>
</template>

<style lang="scss" src="./PartnerCard.scss"/>

<script lang="ts">

import Partner                    from "@/domains/Partners/Partner";
import EntityPointer              from "@/domains/EntityPointer/EntityPointer";
import PartnersService            from "@/domains/Partners/PartnersService";
import SupplyOffer                from "@/domains/SupplyOffers/SupplyOffer";
import FilesService               from "@/core/services/Files/FilesService";
import SupplyOffersService        from "@/domains/SupplyOffers/SupplyOffersService";

import PersonsSection             from "@/domains/Persons/PersonsSection/PersonsSection.vue";
import PartnerInfoSection         from "@/domains/Partners/components/PartnerCard/PartnerEditSection/PartnerInfoSection.vue";
import PartnerMainSection         from "@/domains/Partners/components/PartnerCard/PartnerEditSection/PartnerMainSection/PartnerMainSection.vue";
import SupplyOffersRegistry       from "@/domains/SupplyOffers/components/SupplyOffersRegistry/SupplyOffersRegistry.vue";
import OrderPositionsRegistry     from "@/domains/Orders/components/OrderPositionsRegistry/OrderPositionsRegistry.vue";

export default defineComponent({
  name: 'PartnerCard',

  components: {
    SupplyOffersRegistry,
    OrderPositionsRegistry,
    PartnerInfoSection,
    PartnerMainSection,
    PersonsSection
  },

  emits: ['partner:created'],

  props: {
    /** ID партнёра — передаётся диалоговой системой (роутер/deep link). */
    id: {
      type:    Number,
      default: null,
    },
    /**
     * Партнёр (объект из реестра) или его id. В любом случае карточка грузит
     * полного партнёра с сервера по id — из объекта берётся только id.
     * Пустой Partner без id означает режим создания.
     */
    partnerOrId: {
      type:    [Partner, Number] as PropType<Partner | number | null>,
      default: null,
    },
  },

  setup() {
    return {
      FilesService,
      PartnersService,
    }
  },

  data() {
    return {
      tabs: [] as ReturnType<typeof PartnersService.getEditSectionTabs>,
      activeTab: null as { id: string; name?: string; title?: string } | null,
      localPartner: null as Partner | null,
      supplyOffers: [] as SupplyOffer[],
      /**
       * Режим редактирования существующего партнёра. Выставляется только в init(),
       * а не вычисляется от localPartner.isExisting: add() проставляет id в
       * localPartner, и карточка создания переключалась бы в режим редактирования
       * без табов (activeTab === null) до закрытия через resolve().
       */
      editMode: false,

      /** Партнёр менялся из секций — при закрытии хост должен обновиться */
      isChanged: false,
    }
  },

  async mounted() {
    await this.init();
  },

  computed: {
    partnerPointer(): EntityPointer | null {
      if (this.localPartner) {
        return new EntityPointer('partner', this.localPartner.id);
      }
      else {
        return null;
      }
    },

    showDeclinedProductBadge() {
      return this.supplyOffers.some(_item => _item.status?.id === '03_declined');
    },

    fileEntities() {
      return [new EntityPointer('partner', this.localPartner!.id, this.localPartner!.name)]
    },
  },

  methods: {
    // Открытие существующего и создание нового — один путь. id берём из
    // пропа/partnerOrId или из уже созданного localPartner.
    async init() {
      const id = this.partnerOrId instanceof Partner
        ? this.partnerOrId.id
        : this.id ?? this.partnerOrId ?? this.localPartner?.id;

      if (id) {
        this.localPartner = await PartnersService.get(id);

        this.tabs = PartnersService.getEditSectionTabs(this.$core.currentPartner.isBuyer);
        this.activeTab = this.tabs[1];
        this.editMode = true;

        await this.loadSupplyOffers();
      }
      else {
        this.localPartner = Partner.fromNew();
        this.editMode = false;
      }
    },

    /**
     * Сабмит футера — он есть только в режиме создания. Созданного партнёра
     * отдаём наверх событием (его ждёт PartnersService.createPartner),
     * resolve() делает VDialogV3.
     */
    async submit(): Promise<boolean> {
      let isCreated = false;

      await this.$refs.infoSection.validate();

      if (this.$refs.infoSection.isValid === false) {
        this.$refs.infoSection.scrollToError();
      }
      else {
        isCreated = await PartnersService.add(this.localPartner);

        if (isCreated === true) {
          this.$emit('partner:created', this.localPartner);
        }
        else {
          // ошибку показал сервис
        }
      }

      return isCreated;
    },

    onClose(): void {
      if (this.isChanged === true) {
        this.$core.dialogs.PartnerCard.resolve();
      }
      else {
        // ничего не меняли — диалог закроет сам VDialogV3
      }
    },

    async loadSupplyOffers() {
      this.supplyOffers = await SupplyOffersService.getList({ seller: this.localPartner });
    },

    async renewPartner() {
      if (this.localPartner?.isExisting === true) {
        this.isChanged    = true;
        this.localPartner = await PartnersService.get(this.localPartner.id);
        this.$refs.dialog?.renewFeed();
      }
    },
  },
})

</script>
